import * as THREE from 'three';
import { ImpactFeedback } from '../feedback/impactFeedback';
import { WristHud } from '../feedback/hud';
import { SessionTimer } from './timer';
import { saveSession } from '../storage/sessions';
import type { SessionStats } from './stats';
import type { DominantHand, DrillType, GuardTuning } from '../config/constants';

/**
 * Live-tunable, mode-specific settings. Each runtime applies the keys it understands
 * and ignores the rest, so `SessionController` can dispatch a settings change without
 * knowing which drill is running.
 */
export type RuntimeSettings = {
  /** Sword hand — flips the mandritto/riverso convention in stroke classification (pell). */
  dominantHand: DominantHand;
  /** Pell: minimum impact tip speed (m/s) for a valid colpo. */
  pellMinSpeed: number;
  /** Pell: minimum swing-arc length (m) for a valid colpo. */
  pellMinArc: number;
  /** Targets: disc size multiplier. */
  targetsSizeFactor: number;
  /** Targets: minimum thrust tip speed (m/s). */
  targetsMinSpeed: number;
  /** Targets: radial spawn distance (m). */
  targetsDistanceM: number;
  /** Targets: target lifetime before auto-miss (ms). */
  targetsTtlMs: number;
  /** Targets: delay between targets (ms). */
  targetsSpawnDelayMs: number;
  /** Thrust (custom): tip gate — fraction of the blade near the punta that counts as a thrust. */
  targetsTipMinT: number;
  /** Arcade: target lifetime before auto-miss (ms). */
  arcadeTtlMs: number;
  /** Arcade: delay between targets (ms). */
  arcadeSpawnDelayMs: number;
  /** Arcade: angular tolerance (deg) on the required cut direction. */
  arcadeCutToleranceDeg: number;
  /** Arcade: disc size multiplier. */
  arcadeSizeFactor: number;
  /** Arcade cut (custom): minimum tip speed (m/s) for a slice to count. */
  arcadeMinSpeed: number;
  /** Guard mode: a (partial) update of the recognition tunables. */
  guardTuning: Partial<GuardTuning>;
};

/**
 * Shared lifecycle of a drill runtime. `SessionController` programs against this
 * interface; adding a drill type means implementing it, not editing the controller.
 */
export interface DrillRuntime {
  /** Which drill this runtime drives (e.g. selects the settings-panel tab). */
  readonly drillType: DrillType;
  init(): Promise<void>;
  start(): void;
  pause(): void;
  resume(): void;
  /** Finalize the session (persisting it when applicable) and return its stats. */
  end(): SessionStats;
  /** Call from renderer.setAnimationLoop. */
  tick(): void;
  dispose(): void;
  /**
   * Live swap of controller assignments (e.g. when user forces dominant hand).
   * Caller is responsible for moving the sword visual to the new controller.
   */
  setControllers(
    swordController: THREE.Object3D,
    offHandController: THREE.Object3D,
    swordInputSource: XRInputSource | null,
  ): void;
  setUserHeightCm(cm: number): void;
  attachInputSource(inputSource: XRInputSource, isSwordHand: boolean): void;
  /** Apply any subset of mode-specific settings; keys for other drills are ignored. */
  applySettings(settings: Partial<RuntimeSettings>): void;
}

type DrillRuntimeBaseDeps = {
  scene: THREE.Scene;
  camera: THREE.Camera;
  swordController: THREE.Object3D;
  offHandController: THREE.Object3D;
};

/**
 * Scaffolding every drill runtime shares: frame clock, session timer, wrist HUD,
 * impact feedback, sword-hand input plumbing, and session persistence.
 */
export abstract class DrillRuntimeBase implements DrillRuntime {
  abstract readonly drillType: DrillType;

  protected readonly clock = new THREE.Clock();
  protected readonly timer = new SessionTimer();
  protected readonly hud: WristHud;
  protected readonly feedback: ImpactFeedback;
  protected readonly swordInput: { controller: THREE.Object3D; inputSource: XRInputSource | null };

  protected constructor(deps: DrillRuntimeBaseDeps) {
    this.hud = new WristHud();
    this.hud.attachTo(deps.offHandController);
    this.swordInput = { controller: deps.swordController, inputSource: null };
    // Impact markers always fade by TTL — including free mode. Free mode used to 'persist'
    // and rely on a "clear all" button to remove them; that button was removed (008b1b7),
    // which left free-mode markers with no way to ever disappear.
    this.feedback = new ImpactFeedback({
      scene: deps.scene,
      camera: deps.camera,
      lifetime: 'ttl',
      swordInputSource: () => this.swordInput.inputSource,
    });
  }

  async init(): Promise<void> {
    await this.feedback.init();
  }

  attachInputSource(inputSource: XRInputSource, isSwordHand: boolean): void {
    if (isSwordHand) this.swordInput.inputSource = inputSource;
  }

  /** The HUD plane is moved here; subclasses reset motion trackers in the hook. */
  setControllers(
    swordController: THREE.Object3D,
    offHandController: THREE.Object3D,
    swordInputSource: XRInputSource | null,
  ): void {
    this.hud.attachTo(offHandController);
    this.swordInput.controller = swordController;
    this.swordInput.inputSource = swordInputSource;
    this.onControllersChanged();
  }

  /** Hook: invalidate any motion state sampled from the previous controller frames. */
  protected abstract onControllersChanged(): void;

  pause(): void {
    this.timer.pause(performance.now());
  }

  resume(): void {
    this.timer.resume(performance.now());
  }

  dispose(): void {
    this.hud.dispose();
  }

  /** Persist a session that is ending now (`startedAt` is back-dated by `elapsedMs`). */
  protected persistSession(args: {
    elapsedMs: number;
    durationMs: number;
    userHeightCm: number;
    interrupted: boolean;
    stats: SessionStats;
  }): void {
    saveSession({
      id: crypto.randomUUID(),
      startedAt: Date.now() - args.elapsedMs,
      durationMs: args.durationMs,
      userHeightCm: args.userHeightCm,
      interrupted: args.interrupted,
      drillType: this.drillType,
      stats: args.stats,
    });
  }

  abstract start(): void;
  abstract end(): SessionStats;
  abstract tick(): void;
  abstract setUserHeightCm(cm: number): void;
  abstract applySettings(settings: Partial<RuntimeSettings>): void;
}
